import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Position = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📍 CSS Position</h1>

      <p className="tutorial-intro">
        The <code>position</code> property in CSS determines how an element is
        placed on the page. It works with the properties <code>top</code>,{" "}
        <code>right</code>, <code>bottom</code>, and <code>left</code> to
        control the exact placement of elements.
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          🌟 Why Use CSS Position?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Positioning is essential when you want to move elements freely, create
          overlays, fix navigation bars, or design advanced layouts.
        </p>
      </div>
      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          🌟 Why Use CSS Position?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Positioning is essential when you want to move elements freely, create
          overlays, fix navigation bars, or design advanced layouts.
        </p>
      </div>

      {/* Topics List */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h3 style={{ color: "#854d0e", marginBottom: "0.8rem" }}>
          📘 Topics Covered in CSS Position:
        </h3>
        <ol
          style={{
            textAlign: "left",
            paddingLeft: "1.2rem",
            lineHeight: "1.8",
          }}
        >
          <li>position: static (default)</li>
          <li>position: relative</li>
          <li>position: absolute</li>
          <li>position: fixed</li>
          <li>position: sticky</li>
        </ol>
      </div>

      {/* Static */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 position: static (default)</h2>
        <p>
          This is the default position. Elements are placed in the normal flow
          of the page and <code>top/right/bottom/left</code> do not apply.
        </p>

        <CodeCopyBlock
          code={`div {
  position: static;
}`}
        />

        <div
          style={{ marginTop: "1rem", background: "#f1f5f9", padding: "10px" }}
        >
          <div
            style={{
              border: "2px solid #64748b",
              padding: "10px",
              margin: "5px",
              background: "#e2e8f0",
            }}
          >
            I am static (default)
          </div>
        </div>
      </div>

      {/* Relative */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 position: relative</h2>
        <p>
          The element is positioned relative to its normal position. You can use
          <code> top/right/bottom/left</code> to move it.
        </p>

        <CodeCopyBlock
          code={`div {
  position: relative;
  top: 20px;
  left: 30px;
}`}
        />

        <div
          style={{ marginTop: "1rem", background: "#fff7ed", padding: "10px" }}
        >
          <div
            style={{
              position: "relative",
              top: "20px",
              left: "30px",
              border: "2px solid #f97316",
              padding: "10px",
              background: "#ffedd5",
            }}
          >
            I am moved relative to normal
          </div>
        </div>
      </div>

      {/* Absolute */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 position: absolute</h2>
        <p>
          The element is positioned relative to its nearest{" "}
          <code>positioned</code> ancestor (not static). If none, it’s relative
          to the page itself.
        </p>

        <CodeCopyBlock
          code={`.container {
  position: relative;
}

.child {
  position: absolute;
  top: 10px;
  right: 10px;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            background: "#f0fdf4",
            padding: "20px",
            position: "relative",
            border: "2px solid #22c55e",
          }}
        >
          <p>Container (relative)</p>
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "#bbf7d0",
              padding: "6px",
              border: "2px solid #16a34a",
            }}
          >
            Absolute Box
          </div>
        </div>
      </div>

      {/* Fixed */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 position: fixed</h2>
        <p>
          A fixed element is positioned relative to the{" "}
          <strong>viewport</strong>. It stays in the same place even when the
          page is scrolled.
        </p>

        <CodeCopyBlock
          code={`div {
  position: fixed;
  bottom: 10px;
  right: 10px;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            border: "2px dashed #3b82f6",
            padding: "10px",
            height: "150px",
            overflowY: "scroll",
          }}
        >
          <p>Scroll inside this box 👇</p>
          <div
            style={{
              height: "300px",
              padding: "5px",
              background: "#eff6ff",
            }}
          >
            Scroll me...
          </div>
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              background: "#bfdbfe",
              border: "2px solid #3b82f6",
              padding: "6px",
              borderRadius: "4px",
            }}
          >
            Fixed Box
          </div>
        </div>
      </div>

      {/* Sticky */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 position: sticky</h2>
        <p>
          A sticky element toggles between <code>relative</code> and{" "}
          <code>fixed</code> depending on the scroll position. It “sticks” to
          the defined edge until its container is out of view.
        </p>

        <CodeCopyBlock
          code={`th {
  position: sticky;
  top: 0;
  background: #facc15;
}`}
        />

        <div
          style={{ marginTop: "1rem", maxHeight: "200px", overflowY: "scroll" }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#fde68a",
                    border: "1px solid #facc15",
                    padding: "8px",
                  }}
                >
                  Sticky Header
                </th>
              </tr>
            </thead>
            <tbody>
              {[...Array(15)].map((_, i) => (
                <tr key={i}>
                  <td
                    style={{
                      border: "1px solid #facc15",
                      padding: "8px",
                      background: i % 2 === 0 ? "#fefce8" : "#fffde7",
                    }}
                  >
                    Row {i + 1}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know about positioning, explore{" "}
          <a href="/css/zindex" className="learn-more">
            CSS z-index
          </a>{" "}
          to control how elements stack on top of each other.
        </p>
      </div>
    </div>
  );
};

export default CSS_Position;
